import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ClientLayout from "../../../components/client/ClientLayout";
import { clientApi } from "../../../api";
import { formatDateAU } from "../../../utils/formatDate";

/**
 * IGT Find Member — staff counter tool.
 *
 * Three ways to find a member on one page:
 *   • Card number  — scan a physical card (barcode) or type manually.
 *   • Member number — scan a digital-wallet QR (we encode the
 *     membership number) or type manually.
 *   • Name + DOB — phonetic search on MeRS, re-validated for exact
 *     name match before accepting.
 *
 * Each input is its own form — barcode / QR scanners that end their
 * payload with Enter will auto-submit just that field. On a hit we
 * render a preview with a ⚠ banner if the member is banned /
 * self-excluded, plus a primary "Open full profile" button.
 */
export default function IgtFindMember() {
  const navigate = useNavigate();

  const [cardNumber, setCardNumber]     = useState("");
  const [memberNumber, setMemberNumber] = useState("");
  const [firstName, setFirstName]       = useState("");
  const [lastName, setLastName]         = useState("");
  const [dob, setDob]                   = useState("");

  const cardRef = useRef(null);

  const [busy, setBusy]         = useState(false);
  const [result, setResult]     = useState(null); // { found, member, restrictions, matchedBy }
  const [error, setError]       = useState("");

  async function runLookup(query, label) {
    setBusy(true); setError(""); setResult(null);
    try {
      const r = await clientApi.igt.findMember(query);
      setResult({ ...r, label });
    } catch (err) {
      setError(err?.message || "Lookup failed");
    }
    setBusy(false);
  }

  function clearAll() {
    setCardNumber(""); setMemberNumber(""); setFirstName(""); setLastName(""); setDob("");
    setResult(null); setError("");
    setTimeout(() => cardRef.current?.focus(), 0);
  }

  const restrictionBanner = (() => {
    const r = result?.restrictions;
    if (!r) return null;
    if (r.isBanned) return { kind: "danger",  text: "⚠ Banned — this member cannot use the membership.", reasons: r.reasons };
    if (r.isSelfExcluded) return { kind: "danger", text: "⚠ Self-exclusion on file — contact the venue.", reasons: r.reasons };
    return null;
  })();

  const m = result?.found ? result.member : null;

  return (
    <ClientLayout>
      <div className="venues-page-header">
        <div>
          <h1>Find member</h1>
          <p className="page-subtitle">IGT — scan a card, type a number, or search by name + DOB</p>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 440px) minmax(0, 1fr)", gap: "1.5rem", alignItems: "start" }}>
        {/* ── Left column: three input forms ─────────────────────── */}
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {/* Card number */}
          <form
            className="settings-card"
            onSubmit={(e) => { e.preventDefault(); if (cardNumber.trim()) runLookup({ cardNumber: cardNumber.trim() }, "card"); }}
          >
            <div className="settings-card-header">
              <h3><ScanIcon /> Scan or type a card number</h3>
              <p>Physical card barcode — scanner auto-submits on Enter.</p>
            </div>
            <div className="form-field">
              <label>Card number</label>
              <input
                ref={cardRef}
                value={cardNumber}
                onChange={(e) => setCardNumber(e.target.value.replace(/\s+/g, ""))}
                placeholder="Scan or type…"
                autoFocus
                inputMode="numeric"
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={busy || !cardNumber.trim()}>
              {busy ? "Looking up…" : "Find by card"}
            </button>
          </form>

          {/* Membership number */}
          <form
            className="settings-card"
            onSubmit={(e) => { e.preventDefault(); if (memberNumber.trim()) runLookup({ memberNumber: memberNumber.trim() }, "member"); }}
          >
            <div className="settings-card-header">
              <h3><ScanIcon /> Membership number or digital-card QR</h3>
              <p>Digital wallet QR encodes the membership number — scanner auto-submits.</p>
            </div>
            <div className="form-field">
              <label>Membership number</label>
              <input
                value={memberNumber}
                onChange={(e) => setMemberNumber(e.target.value.replace(/\s+/g, ""))}
                placeholder="e.g. 56153 or QR contents"
                inputMode="numeric"
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={busy || !memberNumber.trim()}>
              {busy ? "Looking up…" : "Find by number"}
            </button>
          </form>

          {/* Name + DOB */}
          <form
            className="settings-card"
            onSubmit={(e) => {
              e.preventDefault();
              if (firstName.trim() && lastName.trim() && dob) {
                runLookup({ firstName: firstName.trim(), lastName: lastName.trim(), dob }, "name");
              }
            }}
          >
            <div className="settings-card-header">
              <h3>Name + date of birth</h3>
              <p>Phonetic search, then exact match on both names.</p>
            </div>
            <div className="form-field">
              <label>First name</label>
              <input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </div>
            <div className="form-field">
              <label>Last name</label>
              <input value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
            <div className="form-field">
              <label>Date of birth</label>
              <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
            </div>
            <button type="submit" className="btn btn-primary" disabled={busy || !firstName.trim() || !lastName.trim() || !dob}>
              {busy ? "Looking up…" : "Search"}
            </button>
          </form>
        </div>

        {/* ── Right column: result / empty state ─────────────────── */}
        <div>
          {error && <div className="toast toast-error" style={{ marginBottom: "0.75rem" }}>{error}</div>}

          {!result && !error && (
            <div className="empty-state" style={{ borderStyle: "dashed" }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="48" height="48">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <h3>No search yet</h3>
              <p>Scan or type on the left. Results appear here, including ban / self-exclusion flags.</p>
            </div>
          )}

          {result && !result.found && (
            <div className="settings-card">
              <div className="settings-card-header">
                <h3>Not found</h3>
                <p>No member matched that {labelFor(result.matchedBy)}.</p>
              </div>
              <button className="btn btn-outline" onClick={clearAll}>Try again</button>
            </div>
          )}

          {m && (
            <div className="settings-card">
              {restrictionBanner && (
                <div className={`toast ${restrictionBanner.kind === "danger" ? "toast-error" : ""}`} style={{ marginBottom: "1rem" }}>
                  {restrictionBanner.text}
                  {restrictionBanner.reasons?.length > 0 && (
                    <div style={{ fontSize: "0.8rem", opacity: 0.85, marginTop: "0.25rem" }}>
                      {restrictionBanner.reasons.join(", ")}
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1rem" }}>
                <div style={{
                  width: 56, height: 56, borderRadius: 12,
                  background: "var(--success-bg, #10b98122)",
                  color: "var(--success, #10b981)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="24" height="24"><polyline points="20 6 9 17 4 12" /></svg>
                </div>
                <div>
                  <div style={{ fontSize: "1.2rem", fontWeight: 700 }}>
                    {[m.firstName, m.lastName].filter(Boolean).join(" ") || "—"}
                    {m.preferredName && m.preferredName !== m.firstName && (
                      <span style={{ fontSize: "0.9rem", color: "var(--text-muted)", marginLeft: "0.5rem" }}>
                        ({m.preferredName})
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>
                    Matched via {labelFor(result.matchedBy)}
                  </div>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "0.5rem" }}>
                <Field label="Membership #" value={m.membershipNumber ? `#${m.membershipNumber}` : "—"} />
                <Field label="Card #"        value={m.cardNumber || "—"} />
                <Field label="Status"        value={m.membershipStatus || "—"} />
                <Field label="Date of birth" value={m.dateOfBirth ? formatDateAU(m.dateOfBirth) : "—"} />
                <Field label="Gender"        value={m.gender || "—"} />
                <Field label="Email"         value={m.email || "—"} />
                <Field label="Mobile"        value={m.mobile || "—"} />
                <Field label="Telephone"     value={m.telephone || "—"} />
              </div>

              {m.address && (
                <div style={{ marginTop: "1rem", padding: "0.75rem", border: "1px solid var(--border)", borderRadius: 8 }}>
                  <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginBottom: "0.3rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>Address</div>
                  <div style={{ fontSize: "0.9rem" }}>
                    {[m.address.line1, m.address.line2, m.address.city, m.address.state, m.address.postcode].filter(Boolean).join(", ") || "—"}
                  </div>
                </div>
              )}

              {m.identifications?.length > 0 && (
                <div style={{ marginTop: "0.75rem", padding: "0.75rem", border: "1px solid var(--border)", borderRadius: 8 }}>
                  <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginBottom: "0.3rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>Identification</div>
                  {m.identifications.map((id, i) => (
                    <div key={i} style={{ fontSize: "0.85rem" }}>
                      {id.typeName || `Type ${id.typeId}`}: {id.number || "—"}
                      {id.expiry && <span style={{ color: "var(--text-muted)", marginLeft: "0.5rem" }}>exp {formatDateAU(id.expiry)}</span>}
                    </div>
                  ))}
                </div>
              )}

              <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem", flexWrap: "wrap" }}>
                {m.membershipNumber && (
                  <button className="btn btn-primary" onClick={() => navigate(`/client/members/${m.membershipNumber}`)}>
                    Open full profile →
                  </button>
                )}
                <button className="btn btn-outline" onClick={clearAll}>Next member</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </ClientLayout>
  );
}

function Field({ label, value }) {
  return (
    <div className="stat-card" style={{ padding: "0.6rem 0.8rem" }}>
      <span className="stat-label">{label}</span>
      <span style={{ fontSize: "0.9rem", color: "var(--text)", marginTop: "0.25rem" }}>{value}</span>
    </div>
  );
}

function labelFor(matchedBy) {
  return matchedBy === "card_number"   ? "card number"
       : matchedBy === "member_number" ? "membership number"
       : matchedBy === "name_dob"      ? "name + DOB"
       : "lookup";
}

function ScanIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14" style={{ marginRight: "0.35rem", verticalAlign: "-2px" }}>
      <path d="M3 7V5a2 2 0 012-2h2" /><path d="M17 3h2a2 2 0 012 2v2" />
      <path d="M21 17v2a2 2 0 01-2 2h-2" /><path d="M7 21H5a2 2 0 01-2-2v-2" />
      <line x1="7" y1="7" x2="7" y2="17" /><line x1="11" y1="7" x2="11" y2="17" />
      <line x1="15" y1="7" x2="15" y2="17" /><line x1="17" y1="7" x2="17" y2="17" />
    </svg>
  );
}
